import { useClientPortalAPI, useFormikSubmit } from "@certn/api";
import {
  BasicButton,
  ButtonGroup,
  Card,
  CountrySelectFormikField,
  PrimaryButton,
  SelectFormikField,
  SelectItem,
  Text,
  TextInputFormikField,
  ToggleField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Formik, FormikHelpers } from "formik";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { boolean, object, string } from "yup";

import { useMfaEnabled } from "@certn/auth";
import { useFlags } from "launchdarkly-react-client-sdk";
import { LocationCountryWhatsThis } from "../../../../components/LocationCountryWhatsThis";
import { useUser } from "../../../../hooks/useUser";
import {
  RequestorConfigLossConfirmModal,
  RequestorFields,
  additionalNotificationEmailsField,
  applyRequestorFields,
  primaryReviewerField,
  useRequestorRoleMeta,
} from "../requestor";
import { RoleChangePermissionsModal } from "./RoleChangePermissionsModal";

type FormValues = {
  full_name: string | null | undefined;
  email: string | null | undefined;
  role: string | undefined;
  location_country: string;
  use_mfa: boolean | null;
  primary_reviewer: string | null;
  additional_notification_emails: string[];
};

type UserUpdatePayload = Omit<
  FormValues,
  "primary_reviewer" | "additional_notification_emails"
> & {
  primary_reviewer?: string | null;
  additional_notification_emails?: string[];
  keep_direct_permissions?: boolean;
};

type PermissionAction = "apply-role-defaults" | "keep-custom";

type PendingFlow =
  | null
  | {
      type: "config-loss";
      payload: UserUpdatePayload;
      targetRoleName: string;
      helpers: FormikHelpers<FormValues>;
    }
  | {
      type: "role-change";
      payload: UserUpdatePayload;
      targetRoleName: string;
      permissionAction: PermissionAction | null;
      helpers: FormikHelpers<FormValues>;
    };

export function UserDetailsTab() {
  const { userId } = useParams();
  const [showEdit, setShowEdit] = useState(false);
  const [pendingFlow, setPendingFlow] = useState<PendingFlow>(null);
  const { t } = useTranslation();
  const { data: user, mutate } = useClientPortalAPI(
    userId ? `/api/internal/client-portal/users/${userId}/` : null
  );

  const loggedInUser = useUser();
  const { mfaEnabled: mfaEnabledFlag, clientPortalEnableRequestorRole } =
    useFlags();

  const { mfaEnabled, clientAccountMfaEnabled } = useMfaEnabled({ user });

  const roles = user?.roles || [];
  const rolesList = [...new Set(roles.map(({ name }) => name))].join(", ");

  const { submit, isSubmitting: isUpdatingUser } = useFormikSubmit(
    `/api/internal/client-portal/users/${userId}/info/update/`,
    {
      onSuccess: () => {
        setPendingFlow(null);
        setShowEdit(false);
        mutate();
      },
      showToastOnError: true,
    }
  );

  const { data: rolesData } = useClientPortalAPI(
    "/api/internal/client-portal/roles/"
  );

  const isEditingSelf = loggedInUser?.details?.id === user?.id;
  const userHasDirectPermissions = Boolean(user?.has_direct_permissions);
  const initialRole = user?.roles?.[0]?.id;

  const { requestorRoleId, visibleRoles } = useRequestorRoleMeta({
    roles: rolesData,
    enableRequestorRole: clientPortalEnableRequestorRole,
    initialRoleId: initialRole,
  });

  const validationSchema = useMemo(
    () =>
      object({
        full_name: string(),
        email: string().email(),
        location_country: string().nullable(),
        use_mfa: boolean().nullable(),
        role: string().optional(),
        primary_reviewer: primaryReviewerField(requestorRoleId, t),
        additional_notification_emails: additionalNotificationEmailsField(t),
      }),
    [requestorRoleId, t]
  );

  const buildPayload = (values: FormValues): UserUpdatePayload => {
    const selectedRoleId = isEditingSelf ? initialRole : values.role;
    const base: UserUpdatePayload = {
      ...values,
      use_mfa: clientAccountMfaEnabled ? null : values.use_mfa,
      role: isEditingSelf ? undefined : values.role,
    };
    return applyRequestorFields(base, selectedRoleId, requestorRoleId);
  };

  const openRoleChangeModalOrSubmit = (
    payload: UserUpdatePayload,
    helpers: FormikHelpers<FormValues>,
    targetRoleName: string
  ) => {
    const roleChanged =
      !isEditingSelf &&
      payload.role !== undefined &&
      payload.role !== initialRole;

    if (roleChanged && userHasDirectPermissions) {
      helpers.setSubmitting(false);
      setPendingFlow({
        type: "role-change",
        payload,
        targetRoleName,
        permissionAction: null,
        helpers,
      });
      return;
    }

    return submit(payload, helpers);
  };

  const runRolePermissionChoice = async (
    action: PermissionAction,
    keepDirectPermissions: boolean
  ) => {
    if (pendingFlow?.type !== "role-change") {
      return;
    }

    const { payload, helpers } = pendingFlow;

    setPendingFlow({
      ...pendingFlow,
      permissionAction: action,
    });

    await submit(
      {
        ...payload,
        keep_direct_permissions: keepDirectPermissions,
      },
      helpers
    );

    // On success, onSuccess clears pendingFlow. On API/validation failure,
    // reset the per-button loading state so the user can retry.
    setPendingFlow((current) =>
      current?.type === "role-change" && current.payload === payload
        ? { ...current, permissionAction: null }
        : current
    );
  };

  const additionalEmailCount =
    user?.additional_notification_emails?.length ?? 0;

  return (
    <Card className={styles.card}>
      <div className={styles.container}>
        {showEdit && user ? (
          <Formik<FormValues>
            initialValues={{
              full_name: user.name,
              email: user.email,
              role: initialRole,
              location_country: user.location_country || "",
              use_mfa: mfaEnabled,
              primary_reviewer: user.primary_reviewer?.id ?? null,
              additional_notification_emails:
                user.additional_notification_emails != null
                  ? [...user.additional_notification_emails]
                  : [],
            }}
            validationSchema={validationSchema}
            onSubmit={(values, helpers) => {
              const payload = buildPayload(values);
              const targetRoleName =
                rolesData?.find((role) => role.id === values.role)?.name || "";

              const roleChanged =
                !isEditingSelf &&
                values.role !== undefined &&
                values.role !== initialRole;

              const willRemoveRequestorRole =
                roleChanged &&
                Boolean(requestorRoleId) &&
                initialRole === requestorRoleId;

              if (willRemoveRequestorRole) {
                helpers.setSubmitting(false);
                setPendingFlow({
                  type: "config-loss",
                  payload,
                  targetRoleName,
                  helpers,
                });
                return;
              }

              return openRoleChangeModalOrSubmit(
                payload,
                helpers,
                targetRoleName
              );
            }}
          >
            {({ isSubmitting, values, handleSubmit }) => {
              const isRequestorRole =
                Boolean(requestorRoleId) && values.role === requestorRoleId;
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--space-4)",
                  }}
                >
                  <TextInputFormikField
                    label="Full name"
                    name="full_name"
                    testID="full-name-field"
                    isRequired
                  />
                  <TextInputFormikField
                    label="Email"
                    name="email"
                    testID="email-field"
                    inputMode="email"
                    isRequired
                  />
                  {!isEditingSelf && (
                    <SelectFormikField
                      label="Role"
                      name="role"
                      testID="role-field"
                      isRequired
                    >
                      {visibleRoles?.map((role) => {
                        return (
                          <SelectItem key={role.id} id={role.id}>
                            {role.name}
                          </SelectItem>
                        );
                      })}
                    </SelectFormikField>
                  )}

                  <CountrySelectFormikField
                    name="location_country"
                    label={t("Location Country")}
                    testID="location-country-field"
                    info={<LocationCountryWhatsThis />}
                  />

                  {isRequestorRole ? (
                    <RequestorFields enableStaleReviewerWarning />
                  ) : null}

                  {mfaEnabledFlag && (
                    <ToggleField
                      label="Enable multi-factor authentication"
                      info="Enable multi-factor authentication"
                      name="use_mfa"
                      testID="use-mfa-field"
                      description="Increase security by turning on multi-factor authentication for this account."
                      isDisabled={clientAccountMfaEnabled}
                    >
                      {t("Enable multi-factor authentication")}
                    </ToggleField>
                  )}

                  <ButtonGroup justify="end">
                    <BasicButton
                      onPress={() => setShowEdit(false)}
                      testID="user-details-cancel"
                    >
                      {t("Cancel")}
                    </BasicButton>
                    <PrimaryButton
                      onPress={() => handleSubmit()}
                      isLoading={isSubmitting}
                      testID="user-details-save-changes"
                    >
                      {t("Save changes")}
                    </PrimaryButton>
                  </ButtonGroup>
                </div>
              );
            }}
          </Formik>
        ) : (
          <>
            <div className={styles.item}>
              <Text style="global/heading-medium">
                <Trans>Full name</Trans>
              </Text>
              <Text style="global/body-primary" color="text-weak">
                {user?.name}
              </Text>
            </div>
            <div className={styles.item}>
              <Text style="global/heading-medium">
                <Trans>Email</Trans>
              </Text>
              <Text style="global/body-primary" color="text-weak">
                {user?.email}
              </Text>
            </div>

            <div className={styles.item}>
              <Text style="global/heading-medium">
                <Trans>Role</Trans>
              </Text>
              <Text style="global/body-primary" color="text-weak">
                {rolesList}
              </Text>
            </div>
            {user?.primary_reviewer ? (
              <div className={styles.item}>
                <Text style="global/heading-medium">
                  <Trans>Primary reviewer</Trans>
                </Text>
                <Text style="global/body-primary" color="text-weak">
                  {user.primary_reviewer.name?.trim() ||
                    user.primary_reviewer.email ||
                    "—"}
                </Text>
              </div>
            ) : null}
            {additionalEmailCount > 0 ? (
              <div className={styles.item}>
                <Text style="global/heading-medium">
                  <Trans>Additional notification emails</Trans>
                </Text>
                <Text style="global/body-primary" color="text-weak">
                  {user?.additional_notification_emails?.join(", ")}
                </Text>
              </div>
            ) : null}
            <div className={styles.item}>
              <Text style="global/heading-medium">
                <Trans>Location Country</Trans>
              </Text>
              <Text style="global/body-primary" color="text-weak">
                {user?.location_country || "--"}
              </Text>
            </div>
            {mfaEnabledFlag && (
              <div className={styles.item}>
                <Text style="global/heading-medium">
                  <Trans>Multi-factor status</Trans>
                </Text>
                <Text style="global/body-primary" color="text-weak">
                  {mfaEnabled ? t("Enabled") : t("Disabled")}
                </Text>
              </div>
            )}
          </>
        )}
      </div>
      {!showEdit && (
        <ButtonGroup direction="column" justify="start">
          <BasicButton
            rightIcon="edit"
            iconType="standard"
            size="small"
            testID="user-details-edit-button"
            onPress={() => {
              setShowEdit(true);
            }}
          >
            <Trans>Edit</Trans>
          </BasicButton>
        </ButtonGroup>
      )}
      <RequestorConfigLossConfirmModal
        isOpen={pendingFlow?.type === "config-loss"}
        userName={user?.name?.trim() || user?.email || ""}
        targetRoleName={
          pendingFlow?.type === "config-loss" ? pendingFlow.targetRoleName : ""
        }
        primaryReviewerName={
          user?.primary_reviewer?.name?.trim() ||
          user?.primary_reviewer?.email ||
          null
        }
        additionalNotificationEmailCount={additionalEmailCount}
        isLoading={isUpdatingUser}
        onClose={() => {
          if (!isUpdatingUser) {
            setPendingFlow(null);
          }
        }}
        onConfirm={() => {
          if (pendingFlow?.type !== "config-loss") {
            return;
          }
          openRoleChangeModalOrSubmit(
            pendingFlow.payload,
            pendingFlow.helpers,
            pendingFlow.targetRoleName
          );
        }}
      />
      <RoleChangePermissionsModal
        isOpen={pendingFlow?.type === "role-change"}
        roleName={
          pendingFlow?.type === "role-change" ? pendingFlow.targetRoleName : ""
        }
        isApplyRoleDefaultsLoading={
          isUpdatingUser &&
          pendingFlow?.type === "role-change" &&
          pendingFlow.permissionAction === "apply-role-defaults"
        }
        isKeepCustomPermissionsLoading={
          isUpdatingUser &&
          pendingFlow?.type === "role-change" &&
          pendingFlow.permissionAction === "keep-custom"
        }
        onClose={() => {
          if (!isUpdatingUser) {
            setPendingFlow(null);
          }
        }}
        onApplyRoleDefaults={async () =>
          await runRolePermissionChoice("apply-role-defaults", false)
        }
        onKeepCustomPermissions={async () =>
          await runRolePermissionChoice("keep-custom", true)
        }
      />
    </Card>
  );
}

const styles = {
  card: css({
    padding: "var(--space-5)",
    display: "flex",
    justifyContent: "space-between",
    gap: "var(--space-2)",
    borderRadius: "var(--border-radius-3)",
  }),
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    width: "100%",
  }),
  item: css({
    display: "flex",
    flexDirection: "column",
  }),
};
